import MuiAlert, { AlertProps } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";

interface ToastContextType {
	showToast: (
		message: string | JSX.Element,
		severity: "success" | "error" | "warning" | "info"
	) => void;
}

type ToastItem = {
  id: number;
  message: string | JSX.Element;
  severity: "success" | "error" | "warning" | "info";
  open: boolean;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
	props,
	ref
) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (
      message: string | JSX.Element,
      severity: "success" | "error" | "warning" | "info"
    ) => {
      setToasts((prev) => [
        ...prev,
        { id: Date.now() + Math.random(), message, severity, open: true },
      ]);
    },
    []
  );

	const handleClose = (id: number, reason?: string) => {
		if (reason === "clickaway") return;
		setToasts((prev) =>
			prev.map((toast) =>
				toast.id === id ? { ...toast, open: false } : toast
			)
		);
	};

	const handleExited = (id: number) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	const getAlertStyles = (severity: ToastItem["severity"]) => ({
		width: "100%",
		fontWeight: severity === "error" ? 700 : 600,
		letterSpacing: "0.2px",
		...(severity === "error" ? { backgroundColor: "#d32f2f", color: "#fff" } : {}),
		"& .MuiAlert-message": {
			fontWeight: severity === "error" ? 700 : 600,
		},
		"& .MuiAlert-icon": {
			alignItems: "center",
			padding: "0 6px 0 0",
		},
		"& .MuiAlert-action": {
			alignItems: "center",
			padding: 0,
			marginLeft: "8px",
		},
		"& .MuiAlert-action .MuiButtonBase-root": {
			color: "inherit",
			padding: "4px",
			margin: 0,
			width: "28px",
			height: "28px",
			minWidth: 0,
			borderRadius: "50%",
		},
		"& .MuiAlert-action .MuiButtonBase-root:hover": {
			backgroundColor: severity === "error" ? "rgba(0, 0, 0, 0.18)" : "rgba(0, 0, 0, 0.08)",
		},
		"& .MuiSvgIcon-root": {
			fontSize: "18px",
		},
	});

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			{toasts.map((toast, idx) => (
				<Snackbar
					key={toast.id}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					open={toast.open}
					autoHideDuration={5000}
					onClose={(_, reason) => handleClose(toast.id, reason)}
                    style={{ marginBottom: idx * 68 + 12 }}
					TransitionProps={{ onExited: () => handleExited(toast.id) }}
				>
					<Alert
						onClose={() => handleClose(toast.id)}
						severity={toast.severity}
						closeText="Fechar"
						sx={getAlertStyles(toast.severity)}
					>
						{toast.message}
					</Alert>
				</Snackbar>
			))}
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};
